import type { ContactInput } from '@/lib/contactSchema';

export type SubmitContactResult =
  | { ok: true }
  | { ok: false; kind: 'validation'; fields: Record<string, string> }
  | { ok: false; kind: 'rate_limited'; message: string }
  | { ok: false; kind: 'network' | 'server'; message: string };

const ENDPOINT = import.meta.env.VITE_CONTACT_ENDPOINT;

export async function submitContact(
  input: ContactInput,
): Promise<SubmitContactResult> {
  if (!ENDPOINT) {
    return {
      ok: false,
      kind: 'server',
      message:
        'Contact endpoint is not configured. Email hello@radium.love directly.',
    };
  }

  let res: Response;
  try {
    res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  } catch {
    return {
      ok: false,
      kind: 'network',
      message:
        "We couldn't reach the server. Check your connection and try again.",
    };
  }

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  if (res.ok) return { ok: true };

  if (res.status === 400 && isValidationBody(body)) {
    return { ok: false, kind: 'validation', fields: body.fields };
  }

  if (res.status === 429) {
    return {
      ok: false,
      kind: 'rate_limited',
      message: hasMessage(body)
        ? body.message
        : 'Too many requests. Try again in a minute.',
    };
  }

  return {
    ok: false,
    kind: 'server',
    message: hasMessage(body)
      ? body.message
      : 'Something went wrong on our side. Please email hello@radium.love directly.',
  };
}

function isValidationBody(
  b: unknown,
): b is { error: 'validation'; fields: Record<string, string> } {
  return (
    !!b &&
    typeof b === 'object' &&
    'error' in b &&
    (b as { error: unknown }).error === 'validation' &&
    'fields' in b &&
    typeof (b as { fields: unknown }).fields === 'object'
  );
}

function hasMessage(b: unknown): b is { message: string } {
  return (
    !!b &&
    typeof b === 'object' &&
    'message' in b &&
    typeof (b as { message: unknown }).message === 'string'
  );
}
